import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight } from "@phosphor-icons/react";
import { FadeUp, Overline } from "../components/Reveal";
import { SERVICES, PRODUCTS, PLATFORMS } from "../data/services";
import { AUDIENCES } from "../data/audiences";
import { api } from "../lib/api";

const trackDeck = (type, ref = "") => {
  api.post("/deck-events", {
    type,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    referrer: ref ? `ref:${ref}` : (document.referrer || "direct"),
  }).catch(() => {});
};

const FREE_DOMAINS = ["gmail.com", "yahoo.com", "yahoo.in", "hotmail.com", "outlook.com", "aol.com", "icloud.com", "mail.com", "proton.me", "protonmail.com", "gmx.com", "live.com", "msn.com", "rediffmail.com", "yandex.com", "ymail.com", "rocketmail.com"];

const inputCls = "h-12 bg-muted/50 border-primary/10 focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 w-full rounded-md border px-3 text-sm";

const DeckGate = ({ onGrant, refCode = "" }) => {
  const [form, setForm] = useState({ name: "", title: "", company: "", email: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const domain = form.email.split("@")[1]?.toLowerCase() || "";
    if (FREE_DOMAINS.includes(domain)) {
      setError("Please use your corporate email — free providers like Gmail, Yahoo and Hotmail aren't accepted.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/deck-access", { ...form, ref: refCode });
      sessionStorage.setItem("anxya_deck_access", "1");
      sessionStorage.setItem("anxya_deck_token", data.deck_token);
      onGrant(data.deck_token);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="pt-32 lg:pt-40 pb-24 min-h-screen" data-testid="deck-gate">
      <div className="mx-auto max-w-xl px-6">
        <FadeUp>
          <Overline>Investor & Partner Deck</Overline>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl font-light tracking-tighter text-primary leading-[0.95]">
            Stop feeding elephants. Move at the speed of AI.
          </h1>
          <p className="mt-5 text-base text-muted-foreground leading-relaxed">
            Tell us who you are to view the deck. Corporate email only — Gmail, Yahoo and Hotmail aren't accepted.
          </p>
        </FadeUp>
        <FadeUp delay={0.15}>
          <form onSubmit={submit} data-testid="deck-gate-form" className="mt-10 rounded-3xl border border-primary/10 bg-white p-8 space-y-5">
            {error && <p data-testid="deck-gate-error" className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">{error}</p>}
            <div>
              <label className="text-sm font-medium text-foreground" htmlFor="gate-name">Full name *</label>
              <input id="gate-name" data-testid="deck-gate-name-input" required value={form.name} onChange={set("name")} className={`mt-2 ${inputCls}`} placeholder="Dr. Asha Verma" />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground" htmlFor="gate-title">Title *</label>
              <input id="gate-title" data-testid="deck-gate-title-input" required value={form.title} onChange={set("title")} className={`mt-2 ${inputCls}`} placeholder="VP, Clinical Operations" />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground" htmlFor="gate-company">Company name *</label>
              <input id="gate-company" data-testid="deck-gate-company-input" required value={form.company} onChange={set("company")} className={`mt-2 ${inputCls}`} placeholder="Acme Pharma" />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground" htmlFor="gate-email">Corporate email *</label>
              <input id="gate-email" data-testid="deck-gate-email-input" required type="email" value={form.email} onChange={set("email")} className={`mt-2 ${inputCls}`} placeholder="asha@acmepharma.com" />
            </div>
            <button
              type="submit"
              data-testid="deck-gate-submit-btn"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary h-12 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/20"
            >
              {submitting ? "Verifying…" : "View the Deck"} <ArrowUpRight size={16} weight="bold" />
            </button>
          </form>
        </FadeUp>
      </div>
    </section>
  );
};

const Slide = ({ num, overline, title, children, dark = false, testId }) => (
  <section
    data-testid={testId}
    className={`pitch-slide py-20 lg:py-28 border-b ${dark ? "bg-primary text-white border-white/10" : "border-primary/10"}`}
  >
    <div className="mx-auto max-w-6xl px-6 lg:px-10">
      <div className="flex items-baseline gap-6">
        <span className={`font-display text-6xl lg:text-8xl font-light select-none leading-none ${dark ? "text-white/10" : "text-primary/10"}`}>
          {num}
        </span>
        <div className="flex-1">
          <Overline>{overline}</Overline>
          <h2 className={`mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight ${dark ? "text-white" : "text-primary"}`}>
            {title}
          </h2>
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  </section>
);

const Bullets = ({ items, dark = false }) => (
  <ul className="grid gap-3 sm:grid-cols-2">
    {items.map((b) => (
      <li key={b} className={`flex items-start gap-3 rounded-xl border px-5 py-4 ${dark ? "border-white/15 bg-white/5" : "border-primary/10 bg-white"}`}>
        <span className="mt-2 h-2 w-2 rounded-full bg-accent shrink-0" />
        <span className={`text-sm sm:text-base font-medium leading-relaxed ${dark ? "text-white" : "text-foreground"}`}>{b}</span>
      </li>
    ))}
  </ul>
);

export default function PitchDeck() {
  const [deckToken, setDeckToken] = useState(sessionStorage.getItem("anxya_deck_token") || "");
  const [granted, setGranted] = useState(sessionStorage.getItem("anxya_deck_access") === "1");
  const [masterPrompt, setMasterPrompt] = useState("");
  const [promptError, setPromptError] = useState("");
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref") || "";

  useEffect(() => {
    const prev = document.title;
    document.title = "Anxya Tech Deck";
    return () => { document.title = prev; };
  }, []);

  useEffect(() => {
    if (!granted) return;
    trackDeck("view", ref);
    const onPrint = () => trackDeck("print", ref);
    window.addEventListener("beforeprint", onPrint);
    return () => window.removeEventListener("beforeprint", onPrint);
  }, [granted, ref]);

  useEffect(() => {
    if (!granted || !deckToken) return;
    setPromptError("");
    api.get("/deck-master-prompt", { headers: { Authorization: `Bearer ${deckToken}` } })
      .then(({ data }) => setMasterPrompt(data.prompt))
      .catch(() => {
        setPromptError("Deck access expired. Please verify your corporate email again.");
        sessionStorage.removeItem("anxya_deck_token");
      });
  }, [granted, deckToken]);

  if (!granted) return <DeckGate onGrant={(token) => { setDeckToken(token); setGranted(true); }} refCode={ref} />;

  return (
    <div data-testid="pitch-deck">
      <section className="pitch-slide pt-32 lg:pt-40 pb-20 lg:pb-28 border-b border-primary/10" data-testid="pitch-slide-1">
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <FadeUp>
            <img src="/assets/logo-icon.png" alt="Anxya Tech logo" className="h-16 w-16 object-contain" data-testid="pitch-logo" />
          </FadeUp>
          <FadeUp><Overline>Pitch Deck — 01 / Cover</Overline></FadeUp>
          <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter text-primary leading-[0.95]">
            Stop feeding elephants.<br />Move at the speed of AI.
          </h1>
          <FadeUp delay={0.3}>
            <p className="mt-8 max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed">
              Anxya Tech — Healthcare Products, Solutions & Services. A completely new-age, AI-native healthcare company.
            </p>
          </FadeUp>
          <FadeUp delay={0.45}>
            <div className="mt-10 overflow-hidden rounded-3xl border border-primary/10">
              <img src="/assets/elephant-ai.png" alt="The elephant of legacy IT dissolving into a lightning-fast AI network" className="w-full object-cover" />
            </div>
          </FadeUp>
          <FadeUp delay={0.55}>
            <button
              onClick={() => { trackDeck("print"); window.print(); }}
              data-testid="pitch-print-btn"
              className="print:hidden mt-8 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-white px-6 py-3 text-sm font-semibold text-primary transition-all duration-300 hover:-translate-y-0.5 hover:border-accent"
            >
              Print / Save as PDF <ArrowUpRight size={16} weight="bold" />
            </button>
          </FadeUp>
        </div>
      </section>

      <Slide num="02" overline="About Us" title="A new kind of healthcare company" testId="pitch-slide-2">
        <Bullets items={[
          "AI-native, unconventional products, solutions and services company",
          "Lean, agile, cost-effective, compliant and secure — by design",
          "Support, maintenance, updates and UX are all AI-powered — lightning fast",
          "Not another slow, elephant-sized IT vendor",
        ]} />
      </Slide>

      <Slide num="03" overline="The Problem" title="Legacy vendors do AI coating. Not AI-native." dark testId="pitch-slide-3">
        <Bullets dark items={[
          "Old-gen products, solutions and services companies bolt AI onto legacy systems",
          "Vast, layered delivery teams — slow, costly, year-long rollouts",
          "Downtime, hassles and ballooning IT budgets",
          "The advent of AI has made this model obsolete",
        ]} />
      </Slide>

      <Slide num="04" overline="Value Proposition" title="What AI-native gets you" testId="pitch-slide-4">
        <Bullets items={[
          "Agile by default — iterate in days, not quarters",
          "Faster time to market",
          "Less downtime, more availability",
          "A fraction of conventional cost",
          "Higher accuracy and quality",
          "Compliance built in — auditable and explainable",
          "Radically better user and patient experience",
          "Fewer hassles — AI-based support, maintenance and updates",
        ]} />
      </Slide>

      <Slide num="05" overline="Universal Platform & Products" title="One AI OS. Healthcare first. Every industry next." dark testId="pitch-slide-5">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="rounded-2xl border border-accent bg-accent/15 p-6 lg:col-span-3" data-testid="pitch-universal-platform-card">
            <p className="font-display text-2xl font-medium text-white">Anxya AI OS & PaaS <span className="ml-1 rounded-full bg-accent/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-accent align-middle">Universal Platform</span></p>
            <p className="mt-2 text-sm text-accent">One platform. Every industry. Every function. Every jurisdiction.</p>
            <p className="mt-3 text-xs text-white/60 leading-relaxed">Industry Intelligence + Organization Intelligence dynamically produce agents, workflows, software, assessments, controls and consulting outputs. Anxya Health is the first vertical.</p>
          </div>
          {PLATFORMS.map((p) => (
            <div key={p.slug} className="rounded-2xl border border-accent/50 bg-accent/10 p-6">
              <p className="font-display text-xl font-medium text-white">{p.name} <span className="ml-1 rounded-full bg-accent/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-accent align-middle">Platform</span></p>
              <p className="mt-2 text-sm text-accent">{p.tagline}</p>
              <p className="mt-3 text-xs text-white/60 leading-relaxed">{p.website.replace("https://", "").replace("www.", "")}</p>
            </div>
          ))}
          {PRODUCTS.map((p) => (
            <div key={p.slug} className="rounded-2xl border border-white/15 bg-white/5 p-6">
              <p className="font-display text-xl font-medium text-white">{p.name}</p>
              <p className="mt-2 text-sm text-accent">{p.tagline}</p>
              <p className="mt-3 text-xs text-white/60 leading-relaxed">{p.website.replace("https://", "").replace("www.", "")}</p>
            </div>
          ))}
        </div>
      </Slide>

      <Slide num="06" overline="Our Services" title="Nine disciplines, one continuum of care" testId="pitch-slide-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s) => (
            <div key={s.slug} className="rounded-xl border border-primary/10 bg-white p-5">
              <p className="text-sm font-bold text-primary">{s.name}</p>
              <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">{s.tagline}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-sm font-semibold text-primary">Every one delivered AI-native — lean, fast, compliant, and a fraction of legacy-vendor cost.</p>
      </Slide>

      <Slide num="07" overline="Who We Serve" title="Built for everyone healthcare touches" dark testId="pitch-slide-7">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {AUDIENCES.map((a) => (
            <div key={a.slug} className="rounded-xl border border-white/15 bg-white/5 p-5">
              <p className="text-sm font-bold text-white">{a.name}</p>
              <p className="mt-1.5 text-xs text-white/60 leading-relaxed">{a.tagline}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-sm font-semibold text-accent">One AI-native backbone — from the lab bench to the patient's pocket.</p>
      </Slide>

      <Slide num="08" overline="Partnership" title="Sell, resell, cross-sell, upsell — together" testId="pitch-slide-8">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {["Refer & Earn — fees on every intro", "Resell — margins on all five offerings", "Cross-sell — bundle and grow shared accounts", "Co-build & Upsell — expand together"].map((m) => (
            <div key={m} className="rounded-xl border border-primary/10 bg-white p-5">
              <p className="text-sm font-bold text-primary">{m.split(" — ")[0]}</p>
              <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">{m.split(" — ")[1]}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-sm font-semibold text-primary">AI-native economics — low cost, high quality, built-in compliance, fast time-to-market — mean fat partner margins and better patient experience for end clients.</p>
      </Slide>

      <Slide num="09" overline="Our Offers" title="The part the elephants will never offer" testId="pitch-slide-9">
        <Bullets items={[
          "Rare disease companies and projects — almost free, with credits",
          "Non-revenue-generating companies — special discounts and free credits to G2M",
          "We win when you win — tied to your journey, not your invoice",
          "The more you engage, the more you gain — every engagement compounds",
        ]} />
      </Slide>

      <Slide num="10" overline="Universal AI OS" title="Master system prompt" dark testId="pitch-slide-master-prompt">
        <p className="mb-6 max-w-3xl text-sm sm:text-base text-white/65 leading-relaxed">
          Full operating prompt for the Universal AI OS, AI PaaS, Agent Marketplace, Workflow Marketplace, Software Builder, Consulting Engine, Intelligence Platform and Digital Transformation Platform.
        </p>
        <pre
          data-testid="master-prompt-content"
          className="max-h-[65vh] overflow-y-auto whitespace-pre-wrap rounded-2xl border border-white/15 bg-white/5 p-6 text-xs sm:text-sm leading-relaxed text-white/75 print:max-h-none print:overflow-visible"
        >
          {masterPrompt || promptError || "Unlocking secure master prompt…"}
        </pre>
      </Slide>

      <Slide num="11" overline="Commercial Model" title="Universal platform economics" testId="pitch-slide-commercial-model">
        <Bullets items={[
          "Credit-based usage across agents, workflows, apps, assessments and consulting outputs",
          "Marketplace revenue from published agents, workflows, templates, industry packs and integrations",
          "Enterprise, government and nonprofit workspaces with RBAC, audit, knowledge vaults and deployment controls",
          "Target unit economics: 100% margin over fully loaded agent runtime and platform TCO",
        ]} />
      </Slide>

      <section className="pitch-slide py-20 lg:py-28 bg-primary text-white" data-testid="pitch-slide-12">
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <div className="flex items-baseline gap-6">
            <span className="font-display text-6xl lg:text-8xl font-light text-white/10 select-none leading-none">12</span>
            <div className="flex-1">
              <Overline>Why We Exist</Overline>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">
                Better care. Lighter lives. A higher collective health index.
              </h2>
              <p className="mt-6 max-w-3xl text-base sm:text-lg text-white/70 leading-relaxed">
                Profit was never the measure. Our founder lives from abundance — and has dedicated the rest of his life to society and community. No part-time philanthropy or CSR; it's ingrained in our DNA. Our end goal: improve patient experience and care, and contribute significantly towards improving our collective health index.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/demo" data-testid="pitch-talk-btn" className="inline-flex items-center gap-2 rounded-full bg-accent px-7 py-3.5 text-sm font-semibold text-primary transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/30">
                  Talk to Us <ArrowUpRight size={16} weight="bold" />
                </Link>
                <Link to="/contact" data-testid="pitch-credits-btn" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-accent">
                  Claim Your Free Credits
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="py-16 lg:py-20" data-testid="pitch-invite">
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <FadeUp>
            <Overline>Keep exploring</Overline>
            <div className="mt-6 flex flex-wrap gap-3">
              {[
                { label: "Universal Platform", to: "/universal-ai-os" },
                { label: "Our Products", to: "/products/eaglei" },
                { label: "Our Services", to: "/services" },
                { label: "About Us", to: "/about" },
                { label: "Who We Serve", to: "/audiences/life-sciences" },
                { label: "Insights", to: "/insights" },
              ].map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  data-testid={`pitch-invite-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="inline-flex items-center gap-2 rounded-full border border-primary/15 bg-white px-5 py-2.5 text-sm font-medium text-primary transition-all duration-300 hover:border-accent hover:-translate-y-0.5"
                >
                  {l.label} <ArrowUpRight size={14} weight="bold" />
                </Link>
              ))}
            </div>
            <p className="mt-6 text-xs text-muted-foreground">
              Products live at eaglei.digital · patexcite.com · clinixify.com
            </p>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
